import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Paper,
} from "@mui/material";
import { getApiUrl, getApiHeaders } from "../../config/api";

const HotspotLocations = ({ isTabView }) => {
  const navigate = useNavigate();
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const miniMapRef = useRef(null);
  const buildingLayersRef = useRef([]);

  useEffect(() => {
    fetchLocationData();
  }, []);

  const fetchLocationData = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl("found-item/list"), {
        headers: getApiHeaders(),
      });

      if (!response.ok) throw new Error("Failed to fetch items");

      const data = await response.json();
      const items = data.items || [];

      // Group items by location
      const locationCounts = {};
      items.forEach((item) => {
        const location = item.location_found || "Unknown";
        if (!locationCounts[location]) {
          locationCounts[location] = {
            count: 0,
            items: [],
          };
        }
        locationCounts[location].count++;
        locationCounts[location].items.push(item);
      });

      // Calculate most common item per location
      const formattedData = Object.entries(locationCounts)
        .map(([location, data]) => {
          // Find most common item type in this location
          const typeCounts = data.items.reduce((acc, item) => {
            const type = item.item_type || "Unknown";
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {});

          const mostCommonItem =
            Object.keys(typeCounts).length > 0
              ? Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0][0]
              : "N/A";

          // Get average lat/lng for this location
          let avgLat = null;
          let avgLng = null;
          
          // First try to get coordinates from individual items
          const validItems = data.items.filter(item => {
            const lat = parseFloat(item.latitude);
            const lng = parseFloat(item.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
          });
          
          if (validItems.length > 0) {
            avgLat = validItems.reduce((sum, item) => sum + parseFloat(item.latitude), 0) / validItems.length;
            avgLng = validItems.reduce((sum, item) => sum + parseFloat(item.longitude), 0) / validItems.length;
            console.log(`Location "${location}": ${data.count} items, ${validItems.length} with valid coords from items: (${avgLat.toFixed(6)}, ${avgLng.toFixed(6)})`);
          } else {
            // Try to parse coordinates from location name (format: "lat, lng")
            const coordMatch = location.match(/^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/);
            if (coordMatch) {
              avgLat = parseFloat(coordMatch[1]);
              avgLng = parseFloat(coordMatch[2]);
              console.log(`Location "${location}": ${data.count} items, parsed coords from location name: (${avgLat.toFixed(6)}, ${avgLng.toFixed(6)})`);
            } else {
              console.log(`Location "${location}": ${data.count} items, NO VALID COORDINATES (items or location name)`);
            }
          }

          return {
            location,
            totalItems: data.count,
            mostCommonItem,
            latitude: avgLat,
            longitude: avgLng,
          };
        })
        .sort((a, b) => b.totalItems - a.totalItems); // Sort by most items

      setLocationData(formattedData);
      
      console.log('Location data prepared, calling updateBuildingColors...');
      // Update building colors with location intensity
      updateBuildingColors(formattedData);
    } catch (error) {
      console.error("Error fetching location data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize mini map with grayscale tiles and fetch buildings
  useEffect(() => {
    if (!miniMapRef.current) {
      const map = L.map("mini-heatmap", {
        center: [10.713335, 122.562388],
        zoom: 18,
        zoomControl: false,
        scrollWheelZoom: false,
        dragging: false,
        doubleClickZoom: false,
        touchZoom: false,
      });
      
      // Use grayscale tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap",
        className: "grayscale-tiles",
      }).addTo(map);
      
      miniMapRef.current = map;
      
      // Fetch and draw buildings from OpenStreetMap
      console.log('Initializing map and fetching buildings...');
      fetchBuildings(map).then(() => {
        console.log('Buildings loaded, now fetching location data...');
        // Fetch location data after buildings are loaded
        if (locationData.length === 0) {
          fetchLocationData();
        }
      });
    }

    return () => {
      if (miniMapRef.current) {
        miniMapRef.current.remove();
        miniMapRef.current = null;
      }
    };
  }, []);

  // Fetch all map features from OpenStreetMap Overpass API
  const fetchBuildings = async (map) => {
    try {
      const centerLat = 10.713335;
      const centerLng = 122.562388;
      const radius = 200; // 200 meter radius around center
      
      console.log('Fetching map features from OpenStreetMap...');
      
      // Overpass API query for buildings, pathways, parking, and other areas
      const query = `
        [out:json][timeout:25];
        (
          way["building"](around:${radius},${centerLat},${centerLng});
          way["highway"](around:${radius},${centerLat},${centerLng});
          way["amenity"](around:${radius},${centerLat},${centerLng});
          way["leisure"](around:${radius},${centerLat},${centerLng});
          way["landuse"](around:${radius},${centerLat},${centerLng});
          way["natural"](around:${radius},${centerLat},${centerLng});
          way["area"](around:${radius},${centerLat},${centerLng});
          relation["building"](around:${radius},${centerLat},${centerLng});
        );
        out body;
        >;
        out skel qt;
      `;
      
      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query,
      });
      
      if (!response.ok) {
        console.error('Overpass API request failed:', response.status);
        return;
      }
      
      const data = await response.json();
      console.log('Received map feature data:', data.elements.length, 'elements');
      
      // Create a lookup for nodes
      const nodes = {};
      data.elements.forEach(element => {
        if (element.type === 'node') {
          nodes[element.id] = [element.lat, element.lon];
        }
      });
      
      // Draw all features (buildings, paths, areas, etc.)
      let featureCount = 0;
      data.elements.forEach(element => {
        if (element.type === 'way' && element.nodes) {
          const coordinates = element.nodes
            .map(nodeId => nodes[nodeId])
            .filter(coord => coord);
          
          if (coordinates.length > 2) {
            // Determine if it's a closed polygon or a line (path)
            const isPolygon = coordinates.length > 2 && 
                            element.nodes[0] === element.nodes[element.nodes.length - 1];
            
            let layer;
            if (isPolygon) {
              // Draw as polygon (buildings, areas)
              layer = L.polygon(coordinates, {
                color: '#999',
                fillColor: '#ccc',
                fillOpacity: 0.4,
                weight: 1,
              }).addTo(map);
            } else {
              // Draw as polyline (paths, roads)
              layer = L.polyline(coordinates, {
                color: '#999',
                weight: 3,
                opacity: 0.6,
              }).addTo(map);
            }
            
            buildingLayersRef.current.push({
              layer: layer,
              name: element.tags?.name || element.tags?.highway || element.tags?.amenity || 'Unknown',
              coordinates: coordinates,
              isPolygon: isPolygon,
            });
            featureCount++;
          }
        }
      });
      
      console.log(`Drew ${featureCount} map features (buildings, paths, areas)`);
    } catch (error) {
      console.error('Failed to fetch map features:', error);
    }
  };

  // Update map feature colors based on location intensity
  const updateBuildingColors = (locationStats) => {
    if (!miniMapRef.current) {
      console.log('Cannot update colors: no map');
      return;
    }
    if (locationStats.length === 0) return;

    console.log('Coloring map features with', locationStats.length, 'locations');
    
    // Filter locations with valid coordinates
    const validLocations = locationStats.filter(stat => stat.latitude && stat.longitude);
    console.log('Valid locations with coordinates:', validLocations.length);
    
    if (validLocations.length === 0) {
      console.warn('No locations have valid coordinates!');
      return;
    }
    
    const maxItems = locationStats[0].totalItems;
    let coloredCount = 0;

    // Color map features (buildings, paths, areas) if items are inside or very close
    if (buildingLayersRef.current.length > 0) {
      buildingLayersRef.current.forEach((feature, idx) => {
        let featureItemCount = 0;
        
        validLocations.forEach(stat => {
          const point = { lat: stat.latitude, lng: stat.longitude };
          
          // For polygons, check if point is inside
          if (feature.isPolygon) {
            const isInside = isPointInPolygon(point, feature.coordinates);
            if (isInside) {
              featureItemCount += stat.totalItems;
              console.log(`Feature ${idx} (${feature.name}) contains location "${stat.location}" - INSIDE`);
            }
          }
          
          // For all features, also check proximity
          const leafletPoint = L.latLng(stat.latitude, stat.longitude);
          const featureBounds = feature.layer.getBounds();
          const featureCenter = featureBounds.getCenter();
          const distance = leafletPoint.distanceTo(featureCenter);
          
          if (distance < 25) { // Very close to feature (25 meters)
            if (featureItemCount === 0) { // Don't double-count if already inside
              featureItemCount += stat.totalItems;
              console.log(`Feature ${idx} (${feature.name}) near location "${stat.location}" - ${distance.toFixed(1)}m`);
            }
          }
        });
        
        if (featureItemCount > 0) {
          const intensity = featureItemCount / maxItems;
          const color = getIntensityColor(intensity);
          
          // Color based on feature type
          if (feature.isPolygon) {
            feature.layer.setStyle({
              fillColor: color,
              fillOpacity: 0.7,
              color: color,
              weight: 2,
            });
          } else {
            // For paths/lines
            feature.layer.setStyle({
              color: color,
              weight: 5,
              opacity: 0.8,
            });
          }
          coloredCount++;
          console.log(`Colored feature ${idx} (${feature.name}) with ${featureItemCount} items (intensity: ${intensity.toFixed(2)}, color: ${color})`);
        }
      });
      
      console.log(`Colored ${coloredCount} out of ${buildingLayersRef.current.length} map features`);
    }
  };

  // Point in polygon check (ray casting algorithm)
  const isPointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][1], yi = polygon[i][0]; // lng, lat
      const xj = polygon[j][1], yj = polygon[j][0];
      
      const intersect = ((yi > point.lat) !== (yj > point.lat))
          && (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Get color based on intensity (0-1)
  const getIntensityColor = (intensity) => {
    if (intensity >= 0.8) return "#516d4f"; // Darkest green
    if (intensity >= 0.6) return "#6b8a68"; // Dark green
    if (intensity >= 0.4) return "#8bb174"; // Medium green
    if (intensity >= 0.2) return "#a5c38e"; // Light-medium green
    return "#d0e7ba"; // Lightest green
  };

  const handleHeatmapClick = () => {
    navigate("/gso/heatmap");
  };

  if (isTabView) {
    return (
      <Box sx={{ 
        height: "100%", 
        display: "flex", 
        gap: { xs: 1, sm: 2, md: 3 }, 
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
        width: "100%"
      }}>
        {/* Left side - Table */}
        <TableContainer
          sx={{
            flex: { xs: "1 1 auto", md: "1 1 58%" },
            bgcolor: "#fbffe8",
            borderRadius: "15px",
            border: "2px solid #516d4f",
            minWidth: 0
          }}
        >
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: "15%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                  <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>
                    Rank
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: "35%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                  <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>
                    Location
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: "25%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                  <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>
                    Total Items
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ width: "25%", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                  <Typography fontWeight="medium" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem" } }}>
                    Most Common Item
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} hover>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                      <Skeleton variant="text" width="40px" sx={{ mx: "auto" }} height={30} />
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                      <Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} />
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                      <Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} />
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                      <Skeleton variant="text" width="80%" sx={{ mx: "auto" }} height={30} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                locationData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                      <Typography fontWeight="bold" color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }, wordBreak: "break-word" }}>
                      <Typography color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>
                        {item.location}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}>
                      <Typography color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>
                        {item.totalItems}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }, wordBreak: "break-word" }}>
                      <Typography color="#516d4f" variant="h6" sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.25rem" } }}>
                        {item.mostCommonItem}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Right side - Mini Heatmap */}
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "1 1 40%" },
            p: { xs: 2, sm: 3 },
            bgcolor: "#fbffe8",
            borderRadius: "15px",
            border: "2px solid #516d4f",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            cursor: "pointer",
            minWidth: 0,
            minHeight: { xs: "300px", md: "auto" },
          }}
          onClick={handleHeatmapClick}
        >
          <Typography
            variant="h6"
            fontWeight="semibold"
            color="#516d4f"
            sx={{ 
              mb: 2, 
              textAlign: "center",
              fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.25rem" }
            }}
          >
            📍 Hotspot Locations Map
          </Typography>
          
          <Box
            sx={{
              width: "100%",
              height: { xs: 200, sm: 250, md: 300 },
              position: "relative",
            }}
          >
            <Box
              id="mini-heatmap"
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "10px",
                border: "1px solid #516d4f",
                position: "relative",
                overflow: "hidden",
                "& .leaflet-container": {
                  height: "100%",
                  width: "100%",
                  borderRadius: "10px",
                },
                "& .grayscale-tiles": {
                  filter: "grayscale(100%) contrast(1.1)",
                  WebkitFilter: "grayscale(100%) contrast(1.1)",
                },
              }}
            >
              {loading && (
                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(240, 247, 232, 0.9)" }}>
                  <Skeleton variant="rectangular" width="100%" height="100%" />
                </Box>
              )}
            </Box>
            
            {/* Click hint overlay */}
            {!loading && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  bgcolor: "rgba(255, 255, 255, 0.92)",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "8px",
                  border: "1px solid #516d4f",
                  zIndex: 500,
                  pointerEvents: "none",
                }}
              >
                <Typography 
                  variant="caption" 
                  color="#516d4f" 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem" } }}
                >
                  Click for full view →
                </Typography>
              </Box>
            )}
          </Box>

          {/* Intensity legend outside map */}
          {!loading && (
            <Box sx={{ mt: 2, width: "100%", px: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography 
                  variant="caption" 
                  color="#516d4f" 
                  fontWeight="medium"
                  sx={{ fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" } }}
                >
                  Intensity:
                </Typography>
                <Box sx={{ display: "flex", height: "14px", flex: 1, borderRadius: "4px", overflow: "hidden", border: "1px solid #516d4f" }}>
                  <Box sx={{ flex: 1, bgcolor: "#d0e7ba" }} />
                  <Box sx={{ flex: 1, bgcolor: "#a5c38e" }} />
                  <Box sx={{ flex: 1, bgcolor: "#8bb174" }} />
                  <Box sx={{ flex: 1, bgcolor: "#6b8a68" }} />
                  <Box sx={{ flex: 1, bgcolor: "#516d4f" }} />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", px: 0.5 }}>
                <Typography 
                  variant="caption" 
                  color="#7a9677"
                  sx={{ fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" } }}
                >
                  Low
                </Typography>
                <Typography 
                  variant="caption" 
                  color="#7a9677"
                  sx={{ fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" } }}
                >
                  High
                </Typography>
              </Box>
            </Box>
          )}

          {!loading && locationData.length > 0 && (
            <Box sx={{ mt: { xs: 2, sm: 3 }, width: "100%", textAlign: "center", px: 2 }}>
              <Typography variant="body1" color="#516d4f" fontWeight="medium" sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" }, wordBreak: "break-word" }}>
                Top Hotspot: {locationData[0]?.location}
              </Typography>
              <Typography variant="body2" color="#7a9677" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.875rem" } }}>
                {locationData[0]?.totalItems} items found
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

export default HotspotLocations;
